import { useEffect, useState, useRef } from "react";
import { RefreshCw } from "lucide-react";
import { isStandalone, isMobileDevice } from "@/lib/pwa";

// Update pipeline (production only):
//  • Notifications live ONLY on the lock screen (sent by the backend on deploy, shown by
//    the service worker) and ONLY when the app is closed — never inside the open app.
//  • INSTALLED PWA: when the app is open and a new build is detected (or the SW tells us
//    an update is live), we show an in-app "Update Available — Refresh" prompt. Tapping it
//    reloads onto the new build. No auto-reload, no in-app notification, no chime.
//  • WEB: no banner — the website loads the freshest build on navigation / at sign-in.
export default function UpdatePrompt() {
  const [ready, setReady] = useState(false);
  const waitingRef = useRef(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return; // never run/register the SW in dev/preview
    if (!("serviceWorker" in navigator)) return;

    // The refresh banner is for the MOBILE installed app only (no browser refresh button
    // there). Desktop — whether the website OR an installed desktop PWA — never shows it.
    const mobilePwa = isStandalone() && isMobileDevice();

    // Register the SW on BOTH web and PWA so lock-screen Web Push works everywhere.
    navigator.serviceWorker.register("/service-worker.js").then((reg) => {
      if (!mobilePwa) return; // web / desktop: registration only — no banner
      const promote = (w) => {
        if (w && navigator.serviceWorker.controller) { waitingRef.current = w; setReady(true); }
      };
      if (reg.waiting) promote(reg.waiting);
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (nw) nw.addEventListener("statechange", () => { if (nw.state === "installed") promote(reg.waiting || nw); });
      });
      setInterval(() => reg.update().catch(() => {}), 30000);
    }).catch(() => {});

    // Everything below (banner detection) is MOBILE-INSTALLED-APP ONLY.
    if (!mobilePwa) return;

    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    // If a push arrives while the app is open, the SW tells us to show the refresh prompt.
    const onSwMessage = (e) => { if (e.data && e.data.type === "UPDATE_AVAILABLE") setReady(true); };
    navigator.serviceWorker.addEventListener("message", onSwMessage);

    // Build-hash detection — the SW file compares the deployed main bundle to this session's.
    const loadedMain = Array.from(document.querySelectorAll("script[src]"))
      .map((s) => s.getAttribute("src"))
      .find((src) => src && /\/static\/js\/main\.[\w]+\.js/.test(src));

    let stopped = false;
    const checkBuild = async () => {
      if (!loadedMain) return;
      try {
        const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const html = await res.text();
        const m = html.match(/\/static\/js\/main\.[\w]+\.js/);
        if (m && m[0] !== loadedMain && !stopped) setReady(true);
      } catch (e) { console.error("update build-check failed (likely offline, ignored):", e); }
    };
    checkBuild(); // run immediately on open — no waiting
    const iv = setInterval(checkBuild, 30000);
    const onVis = () => { if (document.visibilityState === "visible") checkBuild(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", checkBuild);

    return () => {
      stopped = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", checkBuild);
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      navigator.serviceWorker.removeEventListener("message", onSwMessage);
    };
  }, []);

  if (!ready) return null;

  const refresh = async () => {
    // Clear the cached app shell + assets so the reload pulls the freshly-deployed build.
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) { console.error("cache clear before update failed (ignored):", e); }
    // Nudge any waiting worker to activate (controllerchange will reload via the listener)…
    try { waitingRef.current?.postMessage("SKIP_WAITING"); } catch (e) { console.error("SKIP_WAITING postMessage failed:", e); }
    // …but ALWAYS guarantee a reload — the byte-identical-SW case has no waiting worker.
    setTimeout(() => {
      try { window.location.reload(); } catch { window.location.href = window.location.pathname; }
    }, 700);
  };

  return (
    <div className="fixed inset-x-0 top-0 z-[80] flex justify-center px-3 pt-[max(0.5rem,env(safe-area-inset-top))]" data-testid="update-banner">
      <div className="w-full max-w-md animate-[cddSlideDown_0.4s_cubic-bezier(0.16,1,0.3,1)] rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl">
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-yellow to-brand-orange">
            <RefreshCw className="h-4 w-4 text-black" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold leading-tight text-slate-900">🚀 Update Available</p>
            <p className="text-xs text-slate-500">A new version of Snaggedify is ready — tap Refresh to get the latest.</p>
          </div>
          <button
            onClick={refresh}
            data-testid="update-refresh-btn"
            className="shrink-0 rounded-full bg-gradient-to-r from-brand-yellow to-brand-orange px-4 py-2 text-xs font-bold uppercase tracking-[0.06em] text-black transition-transform hover:scale-105"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}
